import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { currentLocale } from '../i18n';
import {
  paymentProfilesApi,
  revolutBusinessApi,
  PaymentProfile,
  MerchantConfig,
  BusinessConfig,
  BankDetails,
  SettlementAccount,
  PaymentProfileAuditEntry,
  CardProvider,
  BankProvider,
} from '../services/api';
import '../styles/reports.css';
import '../styles/regional-offices.css';
import '../styles/payment-profiles.css';

type TabKey = 'merchant' | 'business' | 'bank' | 'accounts' | 'audit';

interface DraftProfile {
  id?: number;
  name: string;
  currency: string;
  is_active: boolean;
  card_provider: CardProvider;
  bank_provider: BankProvider;
  card_charge_currency: string | null;
  merchant_config: MerchantConfig;
  business_config: BusinessConfig;
  bank_details: BankDetails;
  settlement_accounts: SettlementAccount[];
  office_count?: number;
}

const EMPTY_DRAFT: DraftProfile = {
  name: '',
  currency: 'EUR',
  is_active: true,
  card_provider: 'none',
  bank_provider: 'none',
  card_charge_currency: null,
  merchant_config: {},
  business_config: {},
  bank_details: {},
  settlement_accounts: [],
};

const CURRENCIES = ['EUR', 'USD', 'AED', 'GBP', 'UAH', 'PLN', 'KZT'];

// Provider ids are stored; only the names beside them are interface text.
const CARD_PROVIDER_LABEL_KEYS: Record<CardProvider, string> = {
  'none': 'pprofiles.cardNone',
  'revolut_merchant': 'Revolut Merchant',   // product name — shown as-is
};

const BANK_PROVIDER_LABEL_KEYS: Record<BankProvider, string> = {
  'none': 'pprofiles.bankNone',
  'manual': 'pprofiles.bankManual',
  'revolut_business': 'pprofiles.bankRevolut',
};
const providerLabel = (key: string): string =>
  key.startsWith('pprofiles.') ? i18n.t(key) : key;

// ─── Revolut Business setup buttons (Authorize / Register webhook) ──────────
const BusinessSetupActions: React.FC<{ profileId: number; webhookUrl: string }> = ({ profileId, webhookUrl }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'authorize' | 'webhook' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const startAuthorize = async () => {
    setBusy('authorize'); setMsg(null); setErr(null);
    try {
      const { authorize_url } = await revolutBusinessApi.authorizeUrl(profileId);
      window.open(authorize_url, '_blank', 'noopener');
      setMsg(t('pprofiles.consentOpened'));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t('pprofiles.errAuthorizeUrl'));
    } finally {
      setBusy(null);
    }
  };

  const registerWebhook = async () => {
    setBusy('webhook'); setMsg(null); setErr(null);
    try {
      const res = await revolutBusinessApi.setupWebhook(profileId, webhookUrl);
      setMsg(t('pprofiles.webhookRegistered', {
        secret: (res?.signing_secret || res?.webhook_secret || '').slice(0, 12),
      }));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || t('pprofiles.errWebhook'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="pp-bsetup">
      <div className="pp-bsetup-row">
        <button type="button" className="pp-bsetup-btn" onClick={startAuthorize} disabled={busy !== null}>
          {t(busy === 'authorize' ? 'pprofiles.opening' : 'pprofiles.step1')}
        </button>
        <button type="button" className="pp-bsetup-btn" onClick={registerWebhook} disabled={busy !== null}>
          {t(busy === 'webhook' ? 'pprofiles.registering' : 'pprofiles.step2')}
        </button>
      </div>
      {msg && <div className="pp-bsetup-msg">{msg}</div>}
      {err && <div className="pp-bsetup-err">{err}</div>}
      <div className="pp-bsetup-hint">
        {t('pprofiles.setupHint')}
      </div>
    </div>
  );
};

// ─── Webhook URL display ─────────────────────────────────────────────────────
const WebhookUrlBox: React.FC<{ label: string; url: string; events: string[] }> = ({ label, url, events }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="pp-webhook-box">
      <div className="pp-webhook-label">{label}</div>
      <div className="pp-webhook-url-row">
        <code className="pp-webhook-url">{url}</code>
        <button type="button" className="pp-webhook-copy" onClick={copy}>
          {copied ? t('pprofiles.copied') : t('clients.copy')}
        </button>
      </div>
      <div className="pp-webhook-events">
        {t('pprofiles.subscribeTo')} {events.map(e => <code key={e}>{e}</code>)}
      </div>
    </div>
  );
};

// ─── Reusable secret-aware field ─────────────────────────────────────────────
const SecretField: React.FC<{
  label: string;
  value: string | undefined | null;
  onChange: (v: string) => void;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  isSecret?: boolean;
}> = ({ label, value, onChange, multiline, rows, placeholder, isSecret }) => {
  const { t } = useTranslation();
  const isMasked = value === '****';
  return (
    <div className="offices-form-field">
      <label>
        {label}
        {isSecret && <span className="pp-secret-tag">SECRET</span>}
      </label>
      {multiline ? (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isMasked ? t('pprofiles.storedKeepBlank') : (placeholder || '')}
          rows={rows ?? 3}
          className="pp-textarea"
        />
      ) : (
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={isMasked ? t('pprofiles.storedKeepBlank') : (placeholder || '')}
        />
      )}
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────────────────
const AdminPaymentProfilesPage: React.FC = () => {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<PaymentProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [draft, setDraft] = useState<DraftProfile | null>(null);
  const [tab, setTab] = useState<TabKey>('merchant');
  const [totpCode, setTotpCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState<PaymentProfileAuditEntry[] | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Global FX margin (lives in platform_settings)
  const [fxMargin, setFxMargin] = useState<number | null>(null);
  const [fxEditing, setFxEditing] = useState(false);
  const [fxDraft, setFxDraft] = useState('');
  const [fxTotp, setFxTotp] = useState('');
  const [fxSaving, setFxSaving] = useState(false);
  const [fxError, setFxError] = useState<string | null>(null);

  useEffect(() => {
    paymentProfilesApi.getFxMargin()
      .then(r => setFxMargin(r.margin_pct))
      .catch(() => setFxMargin(null));
  }, []);

  const saveFxMargin = async () => {
    setFxError(null);
    const v = parseFloat(fxDraft);
    if (isNaN(v) || v < 0 || v > 20) { setFxError('Enter a value 0–20'); return; }
    if (!fxTotp.trim()) { setFxError('2FA code required'); return; }
    setFxSaving(true);
    try {
      const r = await paymentProfilesApi.setFxMargin(v, fxTotp);
      setFxMargin(r.margin_pct);
      setFxEditing(false);
      setFxTotp('');
    } catch (e: any) {
      setFxError(e?.response?.data?.detail || t('pprofiles.errSave'));
    } finally {
      setFxSaving(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setProfiles(await paymentProfilesApi.list());
    } catch (e: any) {
      setError(e?.response?.data?.detail || t('pprofiles.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setDraft({ ...EMPTY_DRAFT });
    setTab('merchant');
    setTotpCode(''); setModalError(null); setSuccessMsg(null); setAudit(null);
  };

  const openEdit = (p: PaymentProfile) => {
    setDraft({
      id: p.id,
      name: p.name,
      currency: p.currency,
      is_active: p.is_active,
      card_provider: p.card_provider,
      bank_provider: p.bank_provider,
      card_charge_currency: p.card_charge_currency || null,
      merchant_config: { ...p.merchant_config },
      business_config: { ...p.business_config },
      bank_details: { ...p.bank_details },
      settlement_accounts: [...p.settlement_accounts],
      office_count: p.office_count,
    });
    // Pick a sensible first tab — first one that's actually visible.
    if (p.card_provider !== 'none') setTab('merchant');
    else if (p.bank_provider === 'revolut_business') setTab('business');
    else if (p.bank_provider === 'manual') setTab('bank');
    else setTab('merchant');
    setTotpCode(''); setModalError(null); setSuccessMsg(null); setAudit(null);
  };

  const closeForm = () => { setDraft(null); setModalError(null); };

  const stripMasked = <T extends Record<string, any>>(obj: T): Partial<T> => {
    const out: any = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== '****' && v !== '' && v !== null && v !== undefined) out[k] = v;
    });
    return out;
  };

  const save = async () => {
    if (!draft) return;
    if (!totpCode.trim()) { setModalError(t('pprofiles.err2fa')); return; }
    setSaving(true); setModalError(null); setSuccessMsg(null);
    try {
      if (draft.id) {
        await paymentProfilesApi.update(draft.id, {
          totp_code: totpCode,
          name: draft.name,
          currency: draft.currency,
          is_active: draft.is_active,
          card_provider: draft.card_provider,
          bank_provider: draft.bank_provider,
          card_charge_currency: draft.card_charge_currency,
          merchant_config: stripMasked(draft.merchant_config),
          business_config: stripMasked(draft.business_config),
          bank_details: draft.bank_details,
          settlement_accounts: draft.settlement_accounts,
        });
      } else {
        await paymentProfilesApi.create({
          name: draft.name,
          currency: draft.currency,
          totp_code: totpCode,
          card_provider: draft.card_provider,
          bank_provider: draft.bank_provider,
          card_charge_currency: draft.card_charge_currency,
          merchant_config: stripMasked(draft.merchant_config),
          business_config: stripMasked(draft.business_config),
          bank_details: draft.bank_details,
          settlement_accounts: draft.settlement_accounts,
        });
      }
      setSuccessMsg(t('pprofiles.savedOk'));
      setTotpCode('');
      await load();
      if (draft.id) {
        const updated = await paymentProfilesApi.get(draft.id);
        openEdit(updated);
      } else {
        closeForm();
      }
    } catch (e: any) {
      setModalError(e?.response?.data?.detail || t('pprofiles.errSaveProfile'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft?.id) return;
    if (!totpCode.trim()) { setModalError(t('pprofiles.err2fa')); return; }
    if (!window.confirm(t('pprofiles.confirmDelete', { name: draft.name }))) return;
    setSaving(true); setModalError(null);
    try {
      await paymentProfilesApi.remove(draft.id, totpCode);
      await load();
      closeForm();
    } catch (e: any) {
      setModalError(e?.response?.data?.detail || t('pprofiles.errDelete'));
    } finally {
      setSaving(false);
    }
  };

  const loadAudit = async () => {
    if (!draft?.id) return;
    try {
      setAudit(await paymentProfilesApi.audit(draft.id));
    } catch (e: any) {
      setModalError(e?.response?.data?.detail || t('pprofiles.errAudit'));
    }
  };

  // ESC closes modal
  useEffect(() => {
    if (!draft) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeForm(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [draft]);

  const filtered = profiles.filter(p => {
    if (statusFilter === 'active' && !p.is_active) return false;
    if (statusFilter === 'inactive' && p.is_active) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!p.name.toLowerCase().includes(q) && !p.currency.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('pprofiles.title')}</h1>
          <p className="header-subtitle">{t('pprofiles.subtitle')}</p>
        </div>
        <div className="pp-fx-card">
          <div className="pp-fx-label">{t('pprofiles.fxMargin')}</div>
          {!fxEditing ? (
            <div className="pp-fx-row">
              <div className="pp-fx-value">{fxMargin != null ? `${fxMargin.toFixed(2)}%` : '—'}</div>
              <button
                className="pp-fx-edit-btn"
                onClick={() => { setFxDraft(fxMargin != null ? String(fxMargin) : ''); setFxEditing(true); setFxError(null); }}
              >{t('common.edit')}</button>
            </div>
          ) : (
            <div className="pp-fx-edit">
              <div className="pp-fx-edit-row">
                <input
                  type="number" step="0.1" min="0" max="20"
                  value={fxDraft} onChange={e => setFxDraft(e.target.value)}
                  placeholder="1.50"
                  className="pp-fx-input"
                />
                <span className="pp-fx-pct">%</span>
              </div>
              <input
                type="text" inputMode="numeric" maxLength={6}
                value={fxTotp} onChange={e => setFxTotp(e.target.value.replace(/\D/g, ''))}
                placeholder={t('pprofiles.totpPlaceholder')}
                className="pp-fx-totp"
                autoComplete="off"
              />
              <div className="pp-fx-actions">
                <button className="pp-fx-cancel" onClick={() => { setFxEditing(false); setFxTotp(''); setFxError(null); }} disabled={fxSaving}>{t('common.cancel')}</button>
                <button className="pp-fx-save" onClick={saveFxMargin} disabled={fxSaving}>{fxSaving ? '…' : t('common.save')}</button>
              </div>
              {fxError && <div className="pp-fx-error">{fxError}</div>}
            </div>
          )}
          <div className="pp-fx-hint">{t('pprofiles.fxHint')}</div>
        </div>
      </div>

      {error && (
        <div className="info-box" style={{ marginBottom: '24px', borderLeftColor: '#e74c3c', background: 'rgba(231, 76, 60, 0.1)' }}>
          <strong>{t('clients.errorLabel')}</strong> {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {/* Layout */}
      <div className="reports-layout">
        <aside className="filters-panel">
          <div className="filter-section">
            <h3 className="section-heading">{t('common.search')}</h3>
            <div className="campaign-search-input-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="campaign-search-input"
                placeholder={t('pprofiles.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-section">
            <h3 className="section-heading">{t('offices.status')}</h3>
            <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">{t('pprofiles.allStatuses')}</option>
              <option value="active">{t('pprofiles.stActive')}</option>
              <option value="inactive">{t('pprofiles.stDisabled')}</option>
            </select>
          </div>

          <div className="filter-section" style={{ marginTop: 'auto' }}>
            <button className="btn-primary" style={{ width: '100%' }} onClick={openNew}>
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18, marginRight: 8 }}>
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              {t('pprofiles.newProfile')}
            </button>
          </div>
        </aside>

        <div className="results-area visible">
          {loading ? (
            <div className="offices-loading"><div className="offices-spinner" /></div>
          ) : (
            <div className="offices-table-container">
              <table className="offices-table">
                <thead>
                  <tr>
                    <th>{t('pprofiles.colProfile')}</th>
                    <th>{t('offices.currency')}</th>
                    <th>{t('pprofiles.colProvider')}</th>
                    <th>{t('pprofiles.colBoundOffices')}</th>
                    <th>{t('offices.status')}</th>
                    <th>{t('pprofiles.colUpdated')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                        {t(profiles.length === 0 ? 'pprofiles.noneYet' : 'pprofiles.noMatch')}
                      </td>
                    </tr>
                  ) : filtered.map(p => (
                    <tr key={p.id} onClick={() => openEdit(p)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div className="offices-office-cell">
                          <div className="offices-icon" style={{ background: 'linear-gradient(135deg, #FF6B4A, #E94B6C, #8E3A9A)' }}>
                            <svg viewBox="0 0 24 24" fill="white" width={18} height={18}>
                              <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                            </svg>
                          </div>
                          <div>
                            <div style={{ fontWeight: 600 }}>{p.name}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>id: {p.id}</div>
                          </div>
                        </div>
                      </td>
                      <td>{p.currency}</td>
                      <td style={{ textTransform: 'capitalize' }}>{p.provider_type}</td>
                      <td>{p.office_count || 0}</td>
                      <td>
                        <span className={`status-badge ${p.is_active ? 'active' : 'inactive'}`}>
                          {t(p.is_active ? 'pprofiles.stActive' : 'pprofiles.stDisabled')}
                        </span>
                      </td>
                      <td>{p.updated_at ? new Date(p.updated_at).toLocaleDateString(currentLocale()) : '—'}</td>
                      <td>
                        <button
                          className="offices-action-btn edit"
                          onClick={(e) => { e.stopPropagation(); openEdit(p); }}
                        >{t('common.edit')}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit / Create modal */}
      {draft && (
        <div className="offices-modal-overlay" onClick={closeForm}>
          <div className="offices-modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div className="offices-modal-header">
              <h2>{t(draft.id ? 'pprofiles.editTitle' : 'pprofiles.newTitle')}</h2>
              <button className="offices-modal-close" onClick={closeForm}>×</button>
            </div>

            <div className="offices-modal-body">
              {/* Profile master switch — disables BOTH rails. Lives at the top
                  so it doesn't get visually associated with the card section. */}
              <div className={`pp-active-banner ${draft.is_active ? 'on' : 'off'}`}>
                <label className="pp-active-toggle-row">
                  <input
                    type="checkbox"
                    checked={draft.is_active}
                    onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                  />
                  <div className="pp-active-text">
                    <div className="pp-active-title">
                      {t(draft.is_active ? 'pprofiles.profileActive' : 'pprofiles.profileDisabled')}
                    </div>
                    <div className="pp-active-sub">
                      {t(draft.is_active ? 'pprofiles.activeSub' : 'pprofiles.disabledSub')}
                    </div>
                  </div>
                </label>
              </div>

              {/* Top fields */}
              <div className="offices-form-row">
                <div className="offices-form-field">
                  <label>{t('offices.name')}</label>
                  <input
                    type="text"
                    placeholder="e.g. Sense HUB EUR"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    required
                  />
                </div>
                <div className="offices-form-field">
                  <label>{t('offices.currency')}</label>
                  <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {/* Provider selection */}
              <div className="offices-form-row">
                <div className="offices-form-field">
                  <label>{t('pprofiles.cardProvider')}</label>
                  <select
                    value={draft.card_provider}
                    onChange={(e) => setDraft({ ...draft, card_provider: e.target.value as CardProvider })}
                  >
                    {(Object.keys(CARD_PROVIDER_LABEL_KEYS) as CardProvider[]).map(p => (
                      <option key={p} value={p}>{providerLabel(CARD_PROVIDER_LABEL_KEYS[p])}</option>
                    ))}
                  </select>
                </div>
                <div className="offices-form-field">
                  <label>{t('pprofiles.bankProvider')}</label>
                  <select
                    value={draft.bank_provider}
                    onChange={(e) => setDraft({ ...draft, bank_provider: e.target.value as BankProvider })}
                  >
                    {(Object.keys(BANK_PROVIDER_LABEL_KEYS) as BankProvider[]).map(p => (
                      <option key={p} value={p}>{providerLabel(BANK_PROVIDER_LABEL_KEYS[p])}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Card charge currency — only relevant when card provider is enabled
                  and may need to differ from the profile/balance currency. */}
              {draft.card_provider !== 'none' && (
                <div className="offices-form-row">
                  <div className="offices-form-field">
                    <label>{t('pprofiles.cardChargeCurrency')}</label>
                    <select
                      value={draft.card_charge_currency || ''}
                      onChange={(e) => setDraft({
                        ...draft,
                        card_charge_currency: e.target.value || null,
                      })}
                    >
                      <option value="">{t('pprofiles.sameAsProfile', { currency: draft.currency })}</option>
                      {CURRENCIES.filter(c => c !== draft.currency).map(c => (
                        <option key={c} value={c}>{t('pprofiles.withFx', { currency: c })}</option>
                      ))}
                    </select>
                    <small style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>
                      {t('pprofiles.cardCurrencyHint', { currency: draft.currency })}
                    </small>
                  </div>
                </div>
              )}

              {/* Tabs — only the ones that match the selected providers */}
              <div className="pp-tabs-row">
                {draft.card_provider === 'revolut_merchant' && (
                  <button
                    type="button"
                    className={`pp-tab-btn ${tab === 'merchant' ? 'active' : ''}`}
                    onClick={() => setTab('merchant')}
                  >Revolut Merchant</button>
                )}
                {draft.bank_provider === 'revolut_business' && (
                  <button
                    type="button"
                    className={`pp-tab-btn ${tab === 'business' ? 'active' : ''}`}
                    onClick={() => setTab('business')}
                  >Revolut Business</button>
                )}
                {draft.bank_provider !== 'none' && (
                  <button
                    type="button"
                    className={`pp-tab-btn ${tab === 'bank' ? 'active' : ''}`}
                    onClick={() => setTab('bank')}
                  >{t('pprofiles.tabBank')}</button>
                )}
                {draft.bank_provider === 'revolut_business' && (
                  <button
                    type="button"
                    className={`pp-tab-btn ${tab === 'accounts' ? 'active' : ''}`}
                    onClick={() => setTab('accounts')}
                  >{t('pprofiles.tabAccounts')}</button>
                )}
                {draft.id && (
                  <button
                    type="button"
                    className={`pp-tab-btn ${tab === 'audit' ? 'active' : ''}`}
                    onClick={() => { setTab('audit'); loadAudit(); }}
                  >{t('pprofiles.tabAudit')}</button>
                )}
              </div>

              <div className="pp-tab-pane">
                {tab === 'merchant' && (
                  <>
                    <p className="pp-hint">{t('pprofiles.merchantHint')}</p>
                    {draft.id && (
                      <WebhookUrlBox
                        label={t('pprofiles.merchantWebhookLabel')}
                        url={`${window.location.origin}/api/billing/webhooks/revolut/profile/${draft.id}`}
                        events={['ORDER_AUTHORISED', 'ORDER_COMPLETED', 'ORDER_FAILED', 'ORDER_CANCELLED', 'PAYMENT_METHOD_DETACHED']}
                      />
                    )}
                    <SecretField label={t('pprofiles.fieldApiBase')} value={draft.merchant_config.api_base}
                      onChange={(v) => setDraft({ ...draft, merchant_config: { ...draft.merchant_config, api_base: v } })}
                      placeholder="https://merchant.revolut.com (or sandbox-merchant.revolut.com)" />
                    <SecretField label="Secret key (sk_…)" isSecret value={draft.merchant_config.secret_key}
                      onChange={(v) => setDraft({ ...draft, merchant_config: { ...draft.merchant_config, secret_key: v } })} />
                    <SecretField label="Public key (pk_…)" value={draft.merchant_config.public_key}
                      onChange={(v) => setDraft({ ...draft, merchant_config: { ...draft.merchant_config, public_key: v } })} />
                    <SecretField label="Webhook secret (wsk_…)" isSecret value={draft.merchant_config.webhook_secret}
                      onChange={(v) => setDraft({ ...draft, merchant_config: { ...draft.merchant_config, webhook_secret: v } })} />
                  </>
                )}

                {tab === 'business' && (
                  <>
                    <p className="pp-hint">
                      {t('pprofiles.businessHintPre')}
                      <strong>{t('pprofiles.businessHintBold')}</strong>
                      {t('pprofiles.businessHintPost')}
                    </p>
                    {draft.id && (
                      <>
                        <WebhookUrlBox
                          label={t('pprofiles.businessWebhookLabel')}
                          url={`${window.location.origin}/api/billing/webhooks/revolut-business/profile/${draft.id}`}
                          events={['TransactionCreated', 'TransactionStateChanged']}
                        />
                        <BusinessSetupActions profileId={draft.id} webhookUrl={`${window.location.origin}/api/billing/webhooks/revolut-business/profile/${draft.id}`} />
                      </>
                    )}
                    <SecretField label={t('pprofiles.fieldApiBase')} value={draft.business_config.api_base}
                      onChange={(v) => setDraft({ ...draft, business_config: { ...draft.business_config, api_base: v } })}
                      placeholder="https://b2b.revolut.com (or sandbox-b2b.revolut.com)" />
                    <SecretField label={t('pprofiles.fieldClientId')} value={draft.business_config.client_id}
                      onChange={(v) => setDraft({ ...draft, business_config: { ...draft.business_config, client_id: v } })} />
                    <SecretField label={t('pprofiles.fieldJwtIss')} value={draft.business_config.jwt_iss}
                      onChange={(v) => setDraft({ ...draft, business_config: { ...draft.business_config, jwt_iss: v } })}
                      placeholder="studio.sense-ads.io" />
                    <SecretField label={t('pprofiles.fieldRedirectUri')} value={draft.business_config.redirect_uri}
                      onChange={(v) => setDraft({ ...draft, business_config: { ...draft.business_config, redirect_uri: v } })}
                      placeholder="https://studio.sense-ads.io/api/billing/revolut-business/oauth-callback" />
                    <SecretField label={t('pprofiles.fieldPrivateKey')} isSecret multiline rows={8} value={draft.business_config.private_key_pem}
                      onChange={(v) => setDraft({ ...draft, business_config: { ...draft.business_config, private_key_pem: v } })}
                      placeholder={"-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"} />
                    <SecretField label="Refresh token (oa_…)" isSecret value={draft.business_config.refresh_token}
                      onChange={(v) => setDraft({ ...draft, business_config: { ...draft.business_config, refresh_token: v } })} />
                    <SecretField label={t('pprofiles.fieldWebhookSecret')} isSecret value={draft.business_config.webhook_secret}
                      onChange={(v) => setDraft({ ...draft, business_config: { ...draft.business_config, webhook_secret: v } })} />
                  </>
                )}

                {tab === 'bank' && (
                  <>
                    <p className="pp-hint">{t('pprofiles.bankHint')}</p>
                    <SecretField label={t('pprofiles.fieldBeneficiary')} value={draft.bank_details.company_name}
                      onChange={(v) => setDraft({ ...draft, bank_details: { ...draft.bank_details, company_name: v } })} />
                    <SecretField label={t('pprofiles.fieldLegalAddress')} value={draft.bank_details.legal_address}
                      onChange={(v) => setDraft({ ...draft, bank_details: { ...draft.bank_details, legal_address: v } })}
                      placeholder={t('pprofiles.fieldLegalAddressPh')} />
                    <SecretField label={t('pprofiles.fieldVatId')} value={draft.bank_details.vat_id}
                      onChange={(v) => setDraft({ ...draft, bank_details: { ...draft.bank_details, vat_id: v } })} />
                    <SecretField label="IBAN" value={draft.bank_details.bank_iban}
                      onChange={(v) => setDraft({ ...draft, bank_details: { ...draft.bank_details, bank_iban: v } })} />
                    <SecretField label={t('pprofiles.fieldBicSwift')} value={draft.bank_details.bank_swift}
                      onChange={(v) => setDraft({ ...draft, bank_details: { ...draft.bank_details, bank_swift: v } })} />
                    <SecretField label={t('pprofiles.fieldBankName')} value={draft.bank_details.bank_name}
                      onChange={(v) => setDraft({ ...draft, bank_details: { ...draft.bank_details, bank_name: v } })} />
                  </>
                )}

                {tab === 'accounts' && (
                  <>
                    <p className="pp-hint">{t('pprofiles.accountsHint')}</p>
                    {draft.settlement_accounts.map((acc, idx) => (
                      <div key={idx} className="pp-acc-row">
                        <input
                          placeholder={t('pprofiles.accountUuid')}
                          value={acc.id}
                          onChange={(e) => {
                            const next = [...draft.settlement_accounts];
                            next[idx] = { ...next[idx], id: e.target.value };
                            setDraft({ ...draft, settlement_accounts: next });
                          }}
                        />
                        <input
                          placeholder="EUR" style={{ width: 70 }}
                          value={acc.currency}
                          onChange={(e) => {
                            const next = [...draft.settlement_accounts];
                            next[idx] = { ...next[idx], currency: e.target.value.toUpperCase() };
                            setDraft({ ...draft, settlement_accounts: next });
                          }}
                        />
                        <input
                          placeholder={t('pprofiles.ibanOptional')}
                          value={acc.iban || ''}
                          onChange={(e) => {
                            const next = [...draft.settlement_accounts];
                            next[idx] = { ...next[idx], iban: e.target.value };
                            setDraft({ ...draft, settlement_accounts: next });
                          }}
                        />
                        <input
                          placeholder={t('pprofiles.nameOptional')}
                          value={acc.name || ''}
                          onChange={(e) => {
                            const next = [...draft.settlement_accounts];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setDraft({ ...draft, settlement_accounts: next });
                          }}
                        />
                        <button
                          type="button"
                          className="pp-acc-del"
                          onClick={() => setDraft({
                            ...draft,
                            settlement_accounts: draft.settlement_accounts.filter((_, i) => i !== idx),
                          })}
                        >×</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ marginTop: 8 }}
                      onClick={() => setDraft({
                        ...draft,
                        settlement_accounts: [
                          ...draft.settlement_accounts,
                          { id: '', currency: draft.currency, iban: '', name: '' },
                        ],
                      })}
                    >{t('pprofiles.addAccount')}</button>
                  </>
                )}

                {tab === 'audit' && (
                  <div>
                    {!audit ? (
                      <div className="offices-loading"><div className="offices-spinner" /></div>
                    ) : audit.length === 0 ? (
                      <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('pprofiles.noAudit')}</div>
                    ) : (
                      <table className="offices-table" style={{ fontSize: 12 }}>
                        <thead>
                          <tr><th>{t('pprofiles.colWhen')}</th><th>{t('pprofiles.colWho')}</th><th>{t('pprofiles.colAction')}</th><th>IP</th><th>{t('pprofiles.colDetails')}</th></tr>
                        </thead>
                        <tbody>
                          {audit.map(a => (
                            <tr key={a.id}>
                              <td>{new Date(a.created_at).toLocaleString(currentLocale())}</td>
                              <td>{a.user_email}</td>
                              <td><span className={`pp-action-tag ${a.action}`}>{a.action}</span></td>
                              <td>{a.ip || '—'}</td>
                              <td><code style={{ fontSize: 11 }}>{JSON.stringify(a.details)}</code></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>

              {modalError && <div className="offices-modal-error">{modalError}</div>}
              {successMsg && <div className="pp-success-msg">{successMsg}</div>}
            </div>

            <div className="offices-modal-footer">
              <div className="pp-totp-group">
                <label>{t('pprofiles.totpLabel')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="123456"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  autoComplete="off"
                  className="pp-totp"
                />
              </div>
              <div style={{ flex: 1 }} />
              {draft.id && (
                <button type="button" className="btn-secondary" style={{ color: '#c1392b', borderColor: '#f5cdd2' }} onClick={remove} disabled={saving}>
                  {t('common.delete')}
                </button>
              )}
              <button type="button" className="btn-secondary" onClick={closeForm} disabled={saving}>{t('common.cancel')}</button>
              <button type="button" className="btn-primary" onClick={save} disabled={saving || !draft.name}>
                {saving ? t('pprofiles.savingDots') : t(draft.id ? 'pprofiles.saveChanges' : 'pprofiles.createProfile')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPaymentProfilesPage;
